import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_MESSAGE_BODY_LENGTH, type Message } from '@emr/shared';

import {
  claimThread,
  closeThread,
  getThread,
  messagingErrorMessage,
  replyToThread,
} from '../api/messaging.ts';
import { useOrgMembers } from '../schedule/useOrgMembers.ts';
import { Badge, Button, ErrorText, Panel, TextAreaField } from '../components/primitives.tsx';
import { PatientLabel } from '../components/PatientLabel.tsx';
import { FailureNotice, LoadingNotice } from '../components/AsyncState.tsx';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatDateAndTime } from '../format/formatDateTime.ts';
import { useSession } from '../auth/sessionContext.ts';
import { isClaimable, isClosable } from './threadRules.ts';

/**
 * One open thread: its own header (patient, subject/category, claim/close actions per
 * `threadRules.ts`), the full message list, and a compose box — the right pane of `routes/
 * InboxPage.tsx`'s two-pane layout, or the whole page below `useHasRoomForInboxSplitView`.
 *
 * Fetching `GET /api/inbox/threads/:threadId` marks the thread read for this caller as a server-side
 * side effect (`server/src/messaging/routes.ts`'s `markThreadRead`) — this component does nothing
 * extra to trigger that; it is simply true of every render that reaches the query below.
 */
export function ThreadDetail({
  threadId,
  onBack,
}: {
  readonly threadId: string;
  /** Present only below `useHasRoomForInboxSplitView` — a "back to the list" affordance for the
   * stacked, one-pane-at-a-time mobile layout. `undefined` on desktop, where the list stays visible
   * beside this pane and there is nothing to go "back" to. */
  readonly onBack?: () => void;
}) {
  const { t } = useTranslation('inbox');
  const session = useSession();
  const queryClient = useQueryClient();
  const members = useOrgMembers();
  const [draft, setDraft] = useState('');

  const detailQuery = useQuery({
    queryKey: ['inbox', 'thread', threadId],
    queryFn: ({ signal }: { signal: AbortSignal }) => getThread(threadId, signal),
  });

  // Every mutation below invalidates the whole `['inbox', ...]` cache tree — the thread list's own
  // ordering/badges (updatedAt, status, assignedToUserId) and the nav's unassigned count
  // (`useUnassignedInboxCount`) all depend on exactly the fields these calls change, and a claim/
  // close/reply is rare enough on this surface that one broad invalidation costs nothing worth
  // narrowing.
  function invalidateInbox() {
    void queryClient.invalidateQueries({ queryKey: ['inbox'] });
  }

  const reply = useMutation({
    mutationFn: (body: string) => replyToThread(threadId, { body }),
    onSuccess: () => {
      setDraft('');
      invalidateInbox();
    },
  });

  const claim = useMutation({
    mutationFn: () => claimThread(threadId),
    onSuccess: invalidateInbox,
  });

  const close = useMutation({
    mutationFn: () => closeThread(threadId),
    onSuccess: invalidateInbox,
  });

  if (detailQuery.isPending) return <LoadingNotice label={t('page.detail.loading')} />;
  if (detailQuery.isError) {
    return (
      <FailureNotice
        error={detailQuery.error}
        onRetry={() => {
          void detailQuery.refetch();
        }}
      />
    );
  }

  const { thread, messages } = detailQuery.data;

  function handleSend() {
    const body = draft.trim();
    if (body.length === 0) return;
    reply.mutate(body);
  }

  return (
    <div className="flex flex-col gap-4">
      {onBack !== undefined ? (
        <Button variant="secondary" onClick={onBack} className="self-start">
          {t('page.detail.backToList')}
        </Button>
      ) : null}

      <Panel>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <PatientLabel
              patientId={thread.patientId}
              loadingLabel={t('page.list.loadingPatient')}
              unknownLabel={t('page.list.unknownPatient')}
              linkToChart
            />
            <div className="flex flex-wrap gap-1.5">
              <Badge tone={thread.kind === 'care_team' ? 'accent' : 'neutral'}>
                {t(`page.list.kindBadge.${thread.kind}`)}
              </Badge>
              {thread.category !== null ? (
                <Badge tone="neutral">{t(`page.list.category.${thread.category}`)}</Badge>
              ) : null}
              <Badge tone={thread.status === 'open' ? 'success' : 'neutral'}>
                {t(`page.list.statusBadge.${thread.status}`)}
              </Badge>
            </div>
          </div>

          <p className="m-0 text-base font-medium text-ink break-words [overflow-wrap:anywhere]">
            {thread.subject ?? t('page.list.noSubject')}
          </p>

          {thread.kind === 'care_team' ? (
            <p className="m-0 text-sm text-ink-muted">{t('page.detail.careTeamNotice')}</p>
          ) : null}
          {thread.status === 'closed' ? (
            <p className="m-0 text-sm text-ink-muted">{t('page.detail.closedNotice')}</p>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-1">
            {isClaimable(thread) ? (
              <Button
                variant="secondary"
                pending={claim.isPending}
                onClick={() => {
                  claim.mutate();
                }}
              >
                {claim.isPending ? t('page.detail.claiming') : t('page.detail.claim')}
              </Button>
            ) : null}
            {isClosable(thread) ? (
              <Button
                variant="secondary"
                pending={close.isPending}
                onClick={() => {
                  close.mutate();
                }}
              >
                {close.isPending ? t('page.detail.closing') : t('page.detail.close')}
              </Button>
            ) : null}
          </div>
          {claim.isError ? <ErrorText message={messagingErrorMessage(claim.error)} /> : null}
          {close.isError ? <ErrorText message={messagingErrorMessage(close.error)} /> : null}
        </div>
      </Panel>

      <Panel>
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              viewerUserId={session.userId}
              members={members.data}
            />
          ))}
        </ol>
      </Panel>

      <Panel>
        <div className="flex flex-col gap-2">
          <TextAreaField
            label={t('page.detail.composeLabel')}
            placeholder={t('page.detail.composePlaceholder')}
            value={draft}
            maxLength={MAX_MESSAGE_BODY_LENGTH}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            rows={3}
          />
          {reply.isError ? <ErrorText message={messagingErrorMessage(reply.error)} /> : null}
          <div>
            <Button
              pending={reply.isPending}
              disabled={draft.trim().length === 0}
              onClick={handleSend}
            >
              {reply.isPending ? t('page.detail.sending') : t('page.detail.send')}
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

function MessageBubble({
  message,
  viewerUserId,
  members,
}: {
  readonly message: Message;
  readonly viewerUserId: string;
  readonly members: readonly { readonly userId: string; readonly email: string }[] | undefined;
}) {
  const { t } = useTranslation('inbox');
  const { preference } = useDateTimeFormat();

  const senderLabel =
    message.senderRole === 'patient'
      ? t('page.detail.patient')
      : message.authoredByUserId === viewerUserId
        ? t('page.detail.you')
        : (members?.find((member) => member.userId === message.authoredByUserId)?.email ??
          t('page.detail.staffFallback'));

  return (
    <li
      className={[
        'flex flex-col gap-1 rounded-control border p-3 text-sm',
        message.senderRole === 'staff' ? 'border-accent-text bg-raised' : 'border-line bg-field',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-ink">{senderLabel}</span>
        <span className="text-xs text-ink-muted">
          {formatDateAndTime(message.createdAt, preference)}
        </span>
      </div>
      <p className="m-0 text-ink break-words whitespace-pre-wrap [overflow-wrap:anywhere]">
        {message.body}
      </p>
    </li>
  );
}
